import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchWithAuth } from '../../../services/api';
import { getUnitLabel, getUnitArticle, CondoType } from '../../../utils/terminology';
import { isUnitNumberTaken } from '../../../utils/unitNumber';

interface UnitFormModalProps {
  onClose: () => void;
  onCreated: () => void;
  // The block/quadra this new unit/lote is created under — always known and fixed here,
  // since this modal is only opened from that block's own detail view (LocationDetailView).
  preselectedBlockId: string;
  preselectedBlockName: string;
  // Caps the "Andar" input and decides whether it's shown at all (casa/loteamento condos
  // have no floors) — same rule Units.tsx uses (condoType !== 'casa').
  blockFloors?: number;
  condoType?: CondoType;
  // Numbers this block already holds, so a duplicate is caught while the field is still focused
  // instead of by the backend's 409. Compared normalised (see utils/unitNumber), because "101 "
  // and "101" read identically on every screen a porteiro looks at while being two homes in the
  // resident list. Omitted = no local check; the database still refuses it.
  existingNumbers?: string[];
}

/**
 * Quick "add a unit/lote" modal scoped to a single, already-known block/quadra — the block
 * is fixed (no picker), matching the "auto-select the quadra" request: this is opened from
 * that block's own LocationDetailView panel, so re-picking the block would be redundant.
 * For the full unit form with a free block/street picker, see src/pages/Units.tsx.
 */
export const UnitFormModal: React.FC<UnitFormModalProps> = ({
  onClose,
  onCreated,
  preselectedBlockId,
  preselectedBlockName,
  blockFloors,
  condoType,
  existingNumbers = [],
}) => {
  const unitLabel = getUnitLabel(condoType);
  // Asked, not guessed from the last letter: "Lote" ends in an 'e' and is masculine all the same.
  const article = getUnitArticle(condoType);
  const showFloors = condoType !== 'casa';
  const maxFloor = blockFloors || 50;

  const [number, setNumber] = useState('');
  const [floor, setFloor] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedNumber = number.trim();
    if (!trimmedNumber) {
      setError('Número é obrigatório');
      return;
    }
    if (isUnitNumberTaken(existingNumbers, trimmedNumber)) {
      setError(
        `Já existe ${article.indefinite} ${unitLabel.toLowerCase()} com este número em ${preselectedBlockName}`
      );
      return;
    }

    let floorValue = 0;
    if (showFloors) {
      if (floor.trim() === '') {
        setError('Andar é obrigatório');
        return;
      }
      floorValue = Number(floor);
      if (!Number.isFinite(floorValue) || floorValue < 0) {
        setError('Andar deve ser um número válido (mínimo 0)');
        return;
      }
      if (floorValue > maxFloor) {
        setError(`Máximo ${maxFloor} andares`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetchWithAuth('/api/v1/units', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          block_id: preselectedBlockId,
          number: trimmedNumber,
          floor: floorValue,
          // The condo's own style, not a placeholder. This used to send 'Outro' for every unit
          // it created, and that word then surfaced as the unit's one visible detail on the
          // resident's Minha Residência bar — a "type" nobody chose, describing nothing.
          type: condoType === 'casa' ? 'Casa' : 'Apartamento',
          area: 0,
          is_occupied: false,
          is_active: true,
        }),
      });

      if (response.ok) {
        toast.success(`${unitLabel} criado com sucesso!`);
        onCreated();
        onClose();
      } else {
        try {
          const errData = await response.json();
          setError(errData.error || errData.message || `Erro ao criar ${unitLabel.toLowerCase()}`);
        } catch {
          setError(`Erro ao criar ${unitLabel.toLowerCase()} (${response.status})`);
        }
      }
    } catch {
      setError(`Erro ao criar ${unitLabel.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {article.ordinal} {unitLabel}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Bloco</label>
              <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700">
                {preselectedBlockName}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número d{article.definite} {unitLabel}
              </label>
              <input
                autoFocus
                value={number}
                onChange={(e) => { setNumber(e.target.value); setError(null); }}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 101, 1A, etc."
              />
            </div>

            {showFloors && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Andar</label>
                <input
                  type="number"
                  value={floor}
                  onChange={(e) => setFloor(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: 1"
                  min={0}
                  max={maxFloor}
                />
              </div>
            )}

            {error && (
              <p data-testid="unit-form-error" className="text-red-500 text-sm">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Criar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UnitFormModal;
